import React, { ChangeEvent, useState } from "react";
import { Table } from "evergreen-ui";
import { TableCellChangedHandler } from "../factory";
import { ColumnConfig } from "../config/ColumnConfig";
import { CellValidator } from "../validation/CellValidation";
import { runValidators } from "../validation/resolveValidators";

interface TableEditableCellProps<T> {
    columnConfig: ColumnConfig<T>;
    id: string | number;
    cellValue: string;
    onCellChanged?: TableCellChangedHandler<T>;
    inputType: "number" | "text";
    validators?: CellValidator[];
}

export const TableInputCell = <T,>({
    columnConfig,
    id,
    cellValue,
    onCellChanged,
    inputType,
    validators = [],
}: TableEditableCellProps<T>) => {

    // Used to distinguish between an escape key press and a blur event.
    const escapeKeyPressedRef = React.useRef<boolean>(false);

    const [unsubmittedValue, setUnsubmittedValue ] = useState<string>(cellValue);
    const [validationError, setValidationError] = useState<string | null>(null);

    const resolveCellValue = (value: string): string | number => {
        switch (inputType) {
            case "number":
                return parseFloat(value);
            case "text":
            default:
                return value;
        }
    }

    const submitValueIfNeeded = () => {
        if (cellValue === unsubmittedValue || escapeKeyPressedRef.current) {
            return;
        }

        if (validators.length > 0) {
            const result = runValidators(validators, unsubmittedValue);
            if (!result.valid) {
                setValidationError(result.message ?? "Invalid value");
                return;
            }
        }

        setValidationError(null);

        if (onCellChanged) {
            // The handler returns the resolved value from the page's
            // onUpdateRow. If the edit was rejected, this is the original
            // value. If accepted and normalized (e.g. "us" -> "US"), this
            // is the normalized value.
            const resolved = onCellChanged(
                id,
                columnConfig.columnName as keyof T,
                resolveCellValue(unsubmittedValue),
            );
            // If the resolved value matches the original (i.e. the edit was
            // rejected or a no-op), restore the original formatted `cellValue`
            // rather than stringifying the raw value — `String(1)` would drop
            // the "1.00" formatting applied by the factory's `resolveCellData`.
            if (resolved === resolveCellValue(cellValue)) {
                setUnsubmittedValue(cellValue);
            } else {
                setUnsubmittedValue(String(resolved ?? ""));
            }
        }
    };

    return (
        <Table.Cell className={`jc-body-cell input ${validationError ? "error" : ""}`}>
            <input
                type={inputType}
                value={unsubmittedValue}
                style={columnConfig.textAlign ? { textAlign: columnConfig.textAlign } : undefined}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    // `<input type="number">` fires onChange with `value=""` when the
                    // user types characters that can't be parsed as a number (the
                    // browser drops them visually). Treat this as a true no-op so
                    // blurring doesn't commit an empty value and trigger a failed save.
                    if (inputType === "number" && event.target.validity.badInput) {
                        return;
                    }
                    setUnsubmittedValue(event.target.value);
                    if (validationError) {
                        setValidationError(null);
                    }
                }}
                onBlur={() => {
                    // Update the table cell with the new value.
                    submitValueIfNeeded();

                    // Reset the escape key flag, so that the next onBlur can submit the cell value.
                    escapeKeyPressedRef.current = false;
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === "Escape") {
                        // Reset the input value to the initial value.
                        setUnsubmittedValue(cellValue);
                        setValidationError(null);

                        // Indicate that the escape key is triggering the onBlur event.
                        escapeKeyPressedRef.current = true;

                        // Remove focus from the input (the onBlur callback will be fired).
                        event.currentTarget.blur();
                    } else if (event.key === "Enter") {
                        // Just blur — the onBlur handler will call submitValueIfNeeded().
                        // Calling submit here AND blurring would cause a double-submit
                        // where React defers the second setData updater evaluation,
                        // returning the wrong (unresolved) value.
                        event.currentTarget.blur();
                    }
                }}
            />
            {validationError && (
                <span className="jc-cell-validation-error">{validationError}</span>
            )}
        </Table.Cell>
    )
}
